/* eslint-disable react/no-unescaped-entities */
import AccessibleIcon from '@mui/icons-material/Accessible'
import HearingIcon from '@mui/icons-material/Hearing'
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import HistoryToggleOffIcon from '@mui/icons-material/HistoryToggleOff'
import { memo, useCallback, useMemo, useState } from 'react'
import { useShallow } from 'zustand/react/shallow'
import TextToInput from '../../components/TextToInput/TextToInput.jsx'
import { useAuthentificationStore } from '../../contexts/AuthenticationStore.jsx'
import {
  COLOR_VISION_GROUPS,
  COLOR_VISION_ME,
  COLOR_VISION_MG,
  useDayViewSettingsStore,
} from '../../contexts/DayViewSettingsStore.jsx'
import { useUsersStore } from '../../contexts/UsersStore.jsx'
import {
  BRUSH_COCKTAIL,
  BRUSH_FILMING,
  BRUSH_FILMING_SYMBOL,
  BRUSH_MANAGER_35_DURATION,
  BRUSH_NOSHOW,
} from '../generator/BrushesGenerator/AllBrushes.jsx'
import { useDayStore } from '../../contexts/DayStore.jsx'
import { BabyIcon, CocktailIcon, FlagIcon } from '../../components/ForEscapeIcons.jsx'
import ModalEditSession from './ModalEditSession.jsx'

const DayViewSession = memo(
  ({ dayId, intervalId, canEdit, session, intervalGroups, sequences }) => {
    const [isModalOpen, setIsModalOpen] = useState(false)
    const sequence = sequences.find((seq) => seq.sessions.find((sId) => session._id === sId))
    const { users } = useUsersStore(
      useShallow((state) => ({
        users: state.users,
      }))
    )
    const initials = session?.mg ? users[session.mg]?.initials : ''
    const { user } = useAuthentificationStore(
      useShallow((state) => ({
        user: state.user,
      }))
    )
    const { updateSessionValue } = useDayStore(
      useShallow((state) => ({
        updateSessionValue: state.updateSessionValue,
      }))
    )
    const { colorVision } = useDayViewSettingsStore(
      useShallow((state) => ({
        colorVision: state.colorVision,
      }))
    )
    const sessionGroup = useMemo(
      () => intervalGroups.find((g) => g.email === session?.booking?.email),
      [session, intervalGroups]
    )
    const validate = useCallback(
      (search) => {
        const foundUser = Object.values(users)
          .filter((u) => u.state !== 0)
          .find((u) => u.initials.toUpperCase() === search)
        if (foundUser) {
          updateSessionValue(dayId, intervalId, session?._id, 'mg', foundUser._id)
        } else {
          updateSessionValue(dayId, intervalId, session?._id, 'mg', null)
        }
      },
      [users, updateSessionValue, dayId, intervalId, session?._id]
    )

    const openModal = (e) => {
      if (canEdit) {
        setIsModalOpen(true)
        e.preventDefault()
        return false
      }
    }

    const mgColor = users[session.mg]?.color || null
    let bgColor = null
    if (colorVision === COLOR_VISION_MG) {
      bgColor = mgColor
    }
    if (colorVision === COLOR_VISION_ME && user?._id === session.mg) {
      bgColor = mgColor
    }
    if (colorVision === COLOR_VISION_GROUPS && sessionGroup) {
      bgColor = sessionGroup.color
    }
    if (!session?.available && initials.length === 0) {
      bgColor = 'var(--mui-palette-notavailable-default)'
    }
    if (session?.booking?.isClosing && initials.length === 0) {
      bgColor = 'var(--mui-palette-isclosing-default)'
    }

    let isFermetureWithMG = false
    if (!session.available && initials.length > 0) {
      isFermetureWithMG = 'notAvailable'
    }
    if (session?.booking?.isClosing && initials.length > 0) {
      isFermetureWithMG = 'isClosing'
    }

    const classNameIfBlack = bgColor !== null ? 'black ' : ''
    let classNames = classNameIfBlack
    if (!session?.booking?.nbPlayers && session.category == 'room') classNames += 'bookingNotExist '
    if (!session?.mg && session.category == 'room') classNames += 'gmNotAssigned '
    const needToPay = session.ignoreIsPaid
      ? false
      : session?.booking && !session?.booking?.isPaid && !session?.booking?.isClosing
    const isManager35 = session.duration === BRUSH_MANAGER_35_DURATION
    return (
      <div
        className={`sessionDayView ${isManager35 ? 'manager35' : ''} ${
          isFermetureWithMG ? `diagonal diagonal-${isFermetureWithMG}` : null
        }`}
        style={{ backgroundColor: bgColor }}
        onContextMenu={openModal}
      >
        <TextToInput
          forceDisabled={!canEdit}
          id={session?._id}
          bgColor={bgColor}
          className={`inputDayView ${classNames}`}
          initValue={initials}
          textValue={
            <>
              {initials}
              {session?.booking?.nbPlayers > 0 && (
                <>
                  <wbr />
                  <div className="separator">/</div>
                  {session?.booking?.nbPlayers}
                </>
              )}
            </>
          }
          shouldUppercase
          onBlur={validate}
        />
        {isManager35 && <HistoryToggleOffIcon className="icon iconManager35" />}
        <div className={`roomSpecificities ${classNameIfBlack}`}>
          {session?.booking?.hasChild && <BabyIcon className="icon" />}
          {session?.booking?.hasDisabledPerson && <AccessibleIcon className="icon" />}
          {session?.booking?.hasDeaf && <HearingIcon className="icon" />}
          {session?.booking?.isEnglish && <FlagIcon className="icon" />}
          {session?.events.map((e) => {
            switch (e) {
              case BRUSH_NOSHOW:
                return (
                  <span key={e}>
                    <VisibilityOffIcon
                      className={`${classNameIfBlack} eventSymbol`}
                      style={{
                        width: '0.8rem',
                        height: '0.8rem',
                        color: 'var(--mui-palette-text-primary)',
                      }}
                    />
                  </span>
                )
              case BRUSH_FILMING:
                return (
                  <span key={e} className="eventSymbol">
                    {BRUSH_FILMING_SYMBOL}
                  </span>
                )
              case BRUSH_COCKTAIL:
                return (
                  <span key={e} className="eventSymbol">
                    <CocktailIcon />
                  </span>
                )
            }
          })}
        </div>
        <div className={`note ${classNameIfBlack}`}>{session?.note}</div>
        {session.needLongBreak && <div className="longBreak" />}
        {session.longBreakText && (
          <div className={`longBreakText ${classNameIfBlack}`}>{session.longBreakText}</div>
        )}
        {/* {<div className="shortBreak" />}
        {<div className={`shortBreakText ${classNameIfBlack}`}>{'MCA'}</div>} */}
        {session.needShortBreak && <div className="shortBreak" />}
        {session.shortBreakText && (
          <div className={`shortBreakText ${classNameIfBlack}`}>{session.shortBreakText}</div>
        )}
        {(sequence || needToPay) && (
          <div className="bottomLeft">
            {needToPay && <MonetizationOnIcon className={`icon ${classNameIfBlack}`} />}
            {sequence && (
              <div className={`sequenceLetter ${classNameIfBlack}`}>{sequence.letter}</div>
            )}
          </div>
        )}
        <ModalEditSession
          sequences={sequences}
          session={session}
          dayId={dayId}
          intervalId={intervalId}
          intervalGroups={intervalGroups}
          booking={session.booking || {}}
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
        />
      </div>
    )
  }
)
export default DayViewSession
